import Cookies from "js-cookie";
import { Typo, _ } from "js/vendor";
import firebase from "js/firebase";
import { app } from "js/namespaces";
import { getStaticUrl } from "js/config";
import StorageModel from "../storage/storageModel";
import log from "../logger";
import Api from "js/core/api";
import { Features } from "js/core/models/features";
import { Workspace } from "js/core/models/workspace";
import { isElectron } from "js/config";
import isConnected from "js/core/utilities/isConnected";
import { FeatureType } from "common/features";

const User = StorageModel.extend({
    root: "users",

    initialize() {
        if (this.id) {
            // cookie for analytics
            Cookies.set("userId", this.id);
        }
    },

    load() {
        return StorageModel.prototype.load.apply(this, arguments).then(async () => {
            if (app.user.get("email") !== firebase.auth().currentUser.email) {
                // we found an issue where there are some users (who signed up
                // through google sign in) in our user table that do not have an
                // email even though the auth table does have an email, so this
                // is to backfill the user table with emails from the auth table.
                app.user.update({ email: firebase.auth().currentUser.email });
            }

            // fetch plan permissions from Firebase
            app.user.features = new Features();
            if (isElectron) {
                await app.user.features.loadFakeElectronUserPlanPermissions();
            } else {
                await app.user.features.loadUserPlanPermissions();
            }

            // fetch trial detail from API
            if (isConnected.connected) {
                try {
                    // note: these details apply to the personal workspace only
                    const detail = await Api.trialDetail.get({ t: Date.now().getTime() });
                    app.user.trialStatus = detail.status;
                    app.user.analyticsPersonalPlan = detail.analyticsCurrentPlan;
                    app.user.hasTakenTrial = detail.hasTakenTrial;
                } catch (err) {
                    console.warn(err);
                }
            }

            app.user.workspaces = Object.freeze({
                ..._.mapValues(app.user.get("orgs"), (value, orgId) => {
                    return new Workspace(orgId);
                }),
                "personal": new Workspace("personal")
            });

            // create spellcheck dictionary
            if (!app.dictionary) {
                if (app.user.get("usersDictionary")) {
                    app.dictionary = new UserDictionary({
                        id: app.user.get("usersDictionary")
                    }, { disconnected: isElectron });
                } else if (!window.isPlayer) {
                    // create a new user dictionary
                    app.dictionary = new UserDictionary({
                        userId: app.user.id
                    }, { disconnected: isElectron });
                    app.user.update({ usersDictionary: app.dictionary.id });
                }
            }
        });
    },

    loadMinimum() {
        return StorageModel.prototype.load.apply(this, arguments);
    },

    getEmail() {
        return this.getAuthUser().email;
    },

    getAuthUser() {
        return firebase.auth().currentUser;
    },

    getAvatarUrl() {
        let { photoURL } = this.getAuthUser() || {};
        return this.get("avatarUrl") || photoURL;
    },

    async updateEmailAddress(email) {
        let user = this.getAuthUser();
        await user.updateEmail(email);
        this.update({ email });
        await Api.customers.put({ email });
        return user.sendEmailVerification();
    },

    waitForVerifiedEmail() {
        let user = this.getAuthUser();
        let checkVerifiedInterval;
        return new Promise(resolve => {
            clearInterval(checkVerifiedInterval);
            checkVerifiedInterval = setInterval(async () => {
                if (user) {
                    await user.reload();
                    if (user.emailVerified) {
                        await firebase.auth().currentUser.getIdToken(true);
                        clearInterval(checkVerifiedInterval);
                        resolve();
                    }
                } else {
                    clearInterval(checkVerifiedInterval);
                }
            }, 5000);
        });
    },

    getAnalyticsPlan(workspaceId) {
        if (!workspaceId) {
            throw new Error("workspaceId is required");
        }
        if (workspaceId == "personal") {
            const currentPlan = this.analyticsPersonalPlan;
            if (currentPlan) {
                return currentPlan;
            }
        } else {
            if (this.features.isFeatureEnabled(FeatureType.UPGRADE, workspaceId)) {
                return "organization-basic";
            } else {
                return "organization-pro";
            }
        }
    },

    async toObject() {
        const attrs = await StorageModel.prototype.toObject.call(this);
        //base64 encode images
        await fetch(this.getAuthUser().photoURL).catch(err => {
            log.error(err);
            return null;
        });
        return attrs;
    }
});

const UserDictionary = StorageModel.extend({
    root: "usersDictionary",

    initialize() {
        this.textElementsNeedingSpellcheck = new Set([]);
        this.dictionaryLoadPromise = this.getDictionaryData();
    },

    async fetchFileData(url) {
        const res = await fetch(url);
        const data = await res.text();
        return data;
    },

    async getDictionaryData() {
        const affDataUrl = getStaticUrl("/dictionaries/en_US/en_US.aff");
        const wordsDataUrl = getStaticUrl("/dictionaries/en_US/en_US.dic");
        const [affData, wordsData] = await Promise.all([this.fetchFileData(affDataUrl), this.fetchFileData(wordsDataUrl)]);
        this.typo = new Typo("en_US", affData, wordsData);
        this.forceSpellcheck();
    },

    check(word, textElement) {
        if (!this.typo) {
            this.registerText(textElement);
            return true;
        } else {
            return this.get(word.toLowerCase()) || this.typo.check(word);
        }
    },

    getSuggestions(text) {
        if (this.typo) {
            return this.typo.suggest(text);
        } else {
            return [];
        }
    },

    onLoaded() {
        return this.dictionaryLoadPromise;
    },

    registerText(textElement) {
        this.textElementsNeedingSpellcheck.add(textElement);
    },

    forceSpellcheck() {
        this.textElementsNeedingSpellcheck.forEach(textElement => {
            textElement.spellcheck.checkSpelling();
        });
    }

});

export {
    User,
    UserDictionary
};
